// src/pages/ShareRedirectPage.jsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    doc,
    getDoc,
    updateDoc,
    increment,
    serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";

const PLAY_STORE_URL =
    "https://play.google.com/store/apps/details?id=com.anonymous.goshopadmin";

const APP_STORE_URL =
    "https://apps.apple.com/bj/app/goshop-b2b/id6761507630?l=fr-FR";

const DOWNLOAD_URL = "https://goshopforall.com/download";

function getStoreUrl() {
    const ua = navigator.userAgent || navigator.vendor || window.opera;

    if (/android/i.test(ua)) return PLAY_STORE_URL;

    if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) {
        return APP_STORE_URL;
    }

    return DOWNLOAD_URL;
}

function openDeepLink(appUrl) {
    const a = document.createElement("a");
    a.href = appUrl;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();

    setTimeout(() => {
        try {
            document.body.removeChild(a);
        } catch { }
    }, 300);
}

export default function ShareRedirectPage() {
    const { code } = useParams();

    const [link, setLink] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        let timer;

        async function openLink({ appUrl }) {
            const storeUrl = getStoreUrl();

            setLink({
                appUrl,
                storeUrl,
            });

            openDeepLink(appUrl);

            timer = setTimeout(() => {
                window.location.href = storeUrl;
            }, 1800);
        }

        async function load() {
            try {
                if (!code) {
                    setError("Lien invalide.");
                    return;
                }

                if (code.startsWith("shop-")) {
                    const vendorId = decodeURIComponent(code.replace("shop-", "").trim());

                    if (!vendorId) {
                        setError("Boutique introuvable.");
                        return;
                    }

                    await openLink({
                        appUrl: `goshop://shop/${vendorId}`,
                    });

                    return;
                }

                if (code.startsWith("product-")) {
                    const productId = decodeURIComponent(code.replace("product-", "").trim());

                    if (!productId) {
                        setError("Produit introuvable.");
                        return;
                    }

                    await openLink({
                        appUrl: `goshop://product/${productId}`,
                    });

                    return;
                }
                if (code.startsWith("event-")) {
                    const eventId = decodeURIComponent(code.replace("event-", "").trim());

                    if (!eventId) {
                        setError("Évènement introuvable.");
                        return;
                    }

                    await openLink({
                        appUrl: `goshop://event/${eventId}`,
                    });

                    return;
                }

                const ref = doc(db, "shareLinks", code);
                const snap = await getDoc(ref);

                if (!snap.exists()) {
                    setError("Lien introuvable ou expiré.");
                    return;
                }

                const data = snap.data() || {};

                if (!data.appUrl) {
                    setError("Lien incomplet.");
                    return;
                }

                await updateDoc(ref, {
                    clicks: increment(1),
                    lastClickedAt: serverTimestamp(),
                });

                await openLink({
                    appUrl: data.appUrl,
                });
            } catch (e) {
                console.error("ShareRedirectPage error:", e?.message || e);
                setError("Impossible d’ouvrir ce lien.");
            }
        }

        load();

        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [code]);

    return (
        <div style={styles.page}>
            <div style={styles.card}>
                <div style={styles.logo}>GoShop</div>

                {error ? (
                    <>
                        <h1 style={styles.title}>Lien indisponible</h1>
                        <p style={styles.text}>{error}</p>

                        <a href={DOWNLOAD_URL} style={styles.btn}>
                            Télécharger GoShop
                        </a>
                    </>
                ) : (
                    <>
                        <h1 style={styles.title}>Ouverture de GoShop…</h1>

                        <p style={styles.text}>
                            Si l’application est installée, elle va s’ouvrir automatiquement.
                            Sinon, vous serez redirigé vers Play Store ou App Store.
                        </p>

                        {link?.appUrl && (
                            <a href={link.appUrl} style={styles.btn}>
                                Ouvrir dans l’application
                            </a>
                        )}

                        {link?.storeUrl && (
                            <a href={link.storeUrl} style={styles.btnLight}>
                                Télécharger GoShop
                            </a>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

const styles = {
    page: {
        minHeight: "100vh",
        background:
            "linear-gradient(135deg, rgba(249,115,22,.16), rgba(22,163,74,.13)), #f8fafc",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        fontFamily:
            "system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif",
    },

    card: {
        width: "100%",
        maxWidth: 430,
        background: "#fff",
        borderRadius: 28,
        padding: 28,
        textAlign: "center",
        boxShadow: "0 24px 60px rgba(15,23,42,.14)",
    },

    logo: {
        display: "inline-flex",
        padding: "10px 18px",
        borderRadius: 999,
        background: "#f97316",
        color: "#fff",
        fontWeight: 900,
        marginBottom: 18,
    },

    title: {
        margin: 0,
        fontSize: 24,
        color: "#0f172a",
        fontWeight: 900,
    },

    text: {
        color: "#64748b",
        lineHeight: 1.6,
        margin: "12px 0 22px",
    },

    btn: {
        display: "block",
        background: "#f97316",
        color: "#fff",
        padding: "14px 16px",
        borderRadius: 16,
        textDecoration: "none",
        fontWeight: 900,
        marginBottom: 10,
    },

    btnLight: {
        display: "block",
        background: "#f8fafc",
        color: "#0f172a",
        padding: "14px 16px",
        borderRadius: 16,
        textDecoration: "none",
        fontWeight: 900,
        border: "1px solid #e2e8f0",
    },
};